import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Autocomplete,
  MenuItem,
  Alert,
  Grid,
} from '@mui/material';
import {
  buildCityStateOptions,
  getStateNamesSorted,
  clientToAddFormValues,
  mergeClientFromFormValues,
} from '../../utils/clientMapper';

const initialForm = () => ({
  companyName: '',
  companyAddress: '',
  city: '',
  state: '',
  companyEmail: '',
  concernedPersonName: '',
  concernedPersonMobile: '',
  concernedPersonEmail: '',
  concernedPersonDesignation: '',
  dateOfDiscussion: '',
});

/**
 * @param {boolean} [open] — dialog open; when true, form syncs to initialValues (or clears when none)
 * @param {Object} [initialValues] — prefill from selected client (edit)
 * @param {Object} [existingClient] — when set, Save merges locally without POST (edit)
 */
export default function ClientAddForm({ onProceed, addClient, open = true, initialValues = null, existingClient = null }) {
  const [form, setForm] = useState(() => ({ ...initialForm(), ...clientToAddFormValues(initialValues) }));
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const cityOptions = useMemo(() => buildCityStateOptions(), []);
  const stateOptions = useMemo(() => getStateNamesSorted(), []);

  useEffect(() => {
    if (!open) return;
    setFieldErrors({});
    setSubmitError('');
    if (!initialValues) {
      setForm(initialForm());
    } else {
      setForm({ ...initialForm(), ...clientToAddFormValues(initialValues) });
    }
  }, [open, initialValues]);

  const selectedCityOption = useMemo(() => {
    if (!form.city || !form.state) return null;
    return (
      cityOptions.find(
        (o) =>
          o.cityName === form.city &&
          o.stateName === form.state
      ) || null
    );
  }, [cityOptions, form.city, form.state]);

  const update = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSubmitError('');
  }, []);

  const handleCityPick = useCallback(
    (_, option) => {
      if (!option) {
        update('city', '');
        return;
      }
      setForm((prev) => ({
        ...prev,
        city: option.cityName,
        state: option.stateName,
      }));
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.city;
        delete next.state;
        return next;
      });
      setSubmitError('');
    },
    [update]
  );

  const validate = useCallback(() => {
    const err = {};
    const name = (form.companyName || '').trim();
    const addr = (form.companyAddress || '').trim();
    if (!name) err.companyName = 'Company name is required';
    if (!addr) err.companyAddress = 'Company address is required';
    setFieldErrors(err);
    return Object.keys(err).length === 0;
  }, [form.companyName, form.companyAddress]);

  const handleSubmit = useCallback(async () => {
    setSubmitError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      const trimmed = {
        companyName: form.companyName.trim(),
        companyAddress: form.companyAddress.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        companyEmail: form.companyEmail.trim(),
        concernedPersonName: form.concernedPersonName.trim(),
        concernedPersonMobile: form.concernedPersonMobile.trim(),
        concernedPersonEmail: form.concernedPersonEmail.trim(),
        concernedPersonDesignation: form.concernedPersonDesignation.trim(),
        dateOfDiscussion: form.dateOfDiscussion.trim(),
      };
      if (existingClient) {
        onProceed(mergeClientFromFormValues(existingClient, trimmed));
        setForm(initialForm());
        return;
      }
      const created = await addClient(trimmed);
      onProceed(created);
      setForm(initialForm());
    } catch (e) {
      setSubmitError(e?.message || 'Could not create client');
    } finally {
      setSubmitting(false);
    }
  }, [addClient, existingClient, form, onProceed, validate]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      {submitError && <Alert severity="error">{submitError}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Company name"
            value={form.companyName}
            onChange={(e) => update('companyName', e.target.value)}
            error={Boolean(fieldErrors.companyName)}
            helperText={fieldErrors.companyName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            multiline
            minRows={2}
            label="Company address"
            value={form.companyAddress}
            onChange={(e) => update('companyAddress', e.target.value)}
            error={Boolean(fieldErrors.companyAddress)}
            helperText={fieldErrors.companyAddress}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={cityOptions}
            value={selectedCityOption}
            onChange={handleCityPick}
            getOptionLabel={(o) => o?.label || ''}
            isOptionEqualToValue={(a, b) => a?.key === b?.key}
            filterOptions={(opts, { inputValue: iv }) => {
              const q = iv.trim().toLowerCase();
              if (!q) return opts.slice(0, 200);
              return opts.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 200);
            }}
            renderInput={(params) => (
              <TextField {...params} label="City" placeholder="Search city…" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="State"
            value={form.state}
            onChange={(e) => update('state', e.target.value)}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">
              <em>Select or edit</em>
            </MenuItem>
            {stateOptions.map((st) => (
              <MenuItem key={st.id} value={st.name}>
                {st.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            type="email"
            label="Company email"
            value={form.companyEmail}
            onChange={(e) => update('companyEmail', e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            Concerned person
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Name"
            value={form.concernedPersonName}
            onChange={(e) => update('concernedPersonName', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Mobile"
            value={form.concernedPersonMobile}
            onChange={(e) => update('concernedPersonMobile', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="email"
            label="Email"
            value={form.concernedPersonEmail}
            onChange={(e) => update('concernedPersonEmail', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Designation"
            value={form.concernedPersonDesignation}
            onChange={(e) => update('concernedPersonDesignation', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="Date of discussion"
            value={form.dateOfDiscussion}
            onChange={(e) => update('dateOfDiscussion', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? 'Saving…' : existingClient ? 'Save' : 'Proceed'}
        </Button>
      </Box>
    </Box>
  );
}
